/**
 * Custom Exercise Store
 *
 * Zustand (vanilla) store for custom-exercise create/update operations.
 * Deliberately lean compared with `plan.store` / `workout.store`: the custom
 * exercise form only needs create/update plus per-operation status flags, so
 * there is no list/pagination/sharing surface here.
 *
 * Actions re-throw after recording the error so the form page can keep the user
 * on the form (and surface the error in an Alert) instead of navigating away.
 */

import { createStore } from 'zustand/vanilla';

import { failureMessage } from './failure';
import { registerSessionScoped } from './session-scope';
import { customExerciseService } from '../services/custom-exercise.service';

import type { ID } from '../types';
import type { CustomExercise } from '../types/domain/exercise.types';
import type { CustomExerciseInput } from '../validation/custom-exercise.schemas';

// =============================================================================
// STORE INTERFACES
// =============================================================================

export interface CustomExerciseStoreState {
  // Loading states
  isCreating: boolean;
  isUpdating: boolean;

  // Error states
  createError: string | null;
  updateError: string | null;

  // Data state
  currentExercise: CustomExercise | null;
}

export interface CustomExerciseStoreActions {
  createCustomExercise: (data: CustomExerciseInput) => Promise<void>;
  updateCustomExercise: (id: ID, data: CustomExerciseInput) => Promise<void>;

  clearErrors: () => void;
  reset: () => void;
}

export type CustomExerciseStore = CustomExerciseStoreState &
  CustomExerciseStoreActions;

// =============================================================================
// STORE IMPLEMENTATION
// =============================================================================

export const customExerciseStore = createStore<CustomExerciseStore>((set) => ({
  // =========================================================================
  // INITIAL STATE
  // =========================================================================

  isCreating: false,
  isUpdating: false,
  createError: null,
  updateError: null,
  currentExercise: null,

  // =========================================================================
  // CRUD ACTIONS
  // =========================================================================

  createCustomExercise: async (data: CustomExerciseInput): Promise<void> => {
    set({ isCreating: true, createError: null });

    try {
      const created = await customExerciseService.createExercise(data);
      set({ currentExercise: created });
    } catch (error) {
      const errorMessage = failureMessage(
        error,
        'Failed to create custom exercise'
      );
      set({ createError: errorMessage });
      throw error;
    } finally {
      set({ isCreating: false });
    }
  },

  updateCustomExercise: async (
    id: ID,
    data: CustomExerciseInput
  ): Promise<void> => {
    set({ isUpdating: true, updateError: null });

    try {
      const updated = await customExerciseService.updateExercise(id, data);
      set({ currentExercise: updated });
    } catch (error) {
      const errorMessage = failureMessage(
        error,
        'Failed to update custom exercise'
      );
      set({ updateError: errorMessage });
      throw error;
    } finally {
      set({ isUpdating: false });
    }
  },

  // =========================================================================
  // STATE MANAGEMENT ACTIONS
  // =========================================================================

  clearErrors: (): void => {
    set({ createError: null, updateError: null });
  },

  reset: (): void => {
    set({
      isCreating: false,
      isUpdating: false,
      createError: null,
      updateError: null,
      currentExercise: null,
    });
  },
}));

// =============================================================================
// SELECTORS
// =============================================================================

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const selectCustomExerciseLoading = (state: CustomExerciseStore) => ({
  isCreating: state.isCreating,
  isUpdating: state.isUpdating,
});

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const selectCustomExerciseErrors = (state: CustomExerciseStore) => ({
  createError: state.createError,
  updateError: state.updateError,
});

export const selectCurrentCustomExercise = (
  state: CustomExerciseStore
): CustomExercise | null => state.currentExercise;

// =============================================================================
// SESSION SCOPE
// =============================================================================

registerSessionScoped('customExerciseStore', () => {
  customExerciseStore.getState().reset();
});
